import {
  useTeamworkMe,
  useTasksToday,
  useCalendarToday,
  useRecentTeamworkComments,
  type TeamworkTask,
  type CalendarEvent,
  type RecentComment,
} from "../hooks/useMyWork";
import styles from "./MyWork.module.css";

function formatTime(iso: string | null, allDay: boolean): string {
  if (!iso) return "";
  if (allDay) return "All day";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function stripHtml(html: string | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function TasksPanel({ userId }: { userId: string | null }) {
  const { data, loading, error } = useTasksToday(userId);
  const tasks: TeamworkTask[] = data?.["todo-items"] ?? [];

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Tasks due today</h2>
        {!loading && !error && (
          <span className={styles.panelCount}>{tasks.length}</span>
        )}
      </div>
      {loading && <div className={styles.loading}>Loading tasks…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && tasks.length === 0 && (
        <div className={styles.empty}>Nothing due today.</div>
      )}
      {!loading && !error && tasks.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Task</th>
              <th>Project</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td>
                  <div className={styles.taskName}>{t.content}</div>
                  {t["tasklist-name"] && (
                    <div className={styles.taskMeta}>{t["tasklist-name"]}</div>
                  )}
                </td>
                <td>{t["project-name"] ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CalendarPanel() {
  const { data, loading, error } = useCalendarToday();
  const events: CalendarEvent[] = data?.events ?? [];

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Today's calendar</h2>
        {!loading && !error && (
          <span className={styles.panelCount}>{events.length}</span>
        )}
      </div>
      {loading && <div className={styles.loading}>Loading calendar…</div>}
      {error && (
        <div className={styles.error}>
          {error.includes("No Google access")
            ? "Sign out and sign in again to grant calendar access."
            : error}
        </div>
      )}
      {!loading && !error && events.length === 0 && (
        <div className={styles.empty}>Nothing scheduled today.</div>
      )}
      {!loading && !error && events.length > 0 && (
        <ul className={styles.eventList}>
          {events.map((e) => (
            <li key={e.id} className={styles.event}>
              <span className={styles.eventTime}>
                {formatTime(e.start, e.allDay)}
                {!e.allDay && e.end ? ` – ${formatTime(e.end, false)}` : ""}
              </span>
              <span className={styles.eventTitle}>{e.summary}</span>
              {e.location && (
                <span className={styles.eventMeta}>{e.location}</span>
              )}
              {e.hangoutLink && (
                <a
                  className={styles.eventLink}
                  href={e.hangoutLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Join meeting
                </a>
              )}
              {!e.hangoutLink && e.htmlLink && (
                <a
                  className={styles.eventLink}
                  href={e.htmlLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Calendar
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CommentsPanel() {
  const { data, loading, error } = useRecentTeamworkComments(25);
  const comments: RecentComment[] = data?.activity ?? [];

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Recent comments</h2>
        {!loading && !error && (
          <span className={styles.panelCount}>{comments.length}</span>
        )}
      </div>
      {loading && <div className={styles.loading}>Loading comments…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && comments.length === 0 && (
        <div className={styles.empty}>No recent comments.</div>
      )}
      {!loading && !error && comments.length > 0 && (
        <ul className={styles.commentList}>
          {comments.map((c) => (
            <li key={c.id} className={styles.comment}>
              <div className={styles.commentHead}>
                <span>
                  {c["from-name"] ?? "Someone"}
                  {c["project-name"] ? ` · ${c["project-name"]}` : ""}
                </span>
                {c.datetime && (
                  <span>{new Date(c.datetime).toLocaleString("en-GB")}</span>
                )}
              </div>
              <div className={styles.commentBody}>
                {stripHtml(c.description ?? c["item-info"])}
              </div>
              {c["item-link"] && (
                <a
                  className={styles.commentLink}
                  href={c["item-link"]}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Teamwork
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function MyWork() {
  const me = useTeamworkMe();
  const userId = me.data?.id ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>My Work</h1>
        <p className={styles.subtitle}>
          Today's tasks, calendar, and recent Teamwork activity in one place.
        </p>
        {me.error && (
          <p className={styles.error}>
            Couldn't link your account to Teamwork: {me.error}
          </p>
        )}
      </header>
      <div className={styles.grid}>
        <TasksPanel userId={userId} />
        <CalendarPanel />
        <CommentsPanel />
      </div>
    </div>
  );
}
